<template>
  <div class="main-header" @keydown.esc.prevent="closeOnEsc" ref="mainMenu">
    <!-- Overlay -->
    <transition name="main-menu-fade">
      <div v-show="overlayShow" class="main-header__overlay" />
    </transition>
    <div class="header-shadow">
      <slot name="skipto" />
      <div class="main-header__wrapper">
        <div class="main-header__hamburger">
          <slot name="burger" :burger="burger" :show-mobile="showMobile" />
        </div>
        <nav class="main-header__links">
          <!-- Desktop -->
          <div class="header-links--primary" v-if="!smallScreen">
            <slot
              name="categoryLinks"
              :open="openMenu"
              :close="closeMenuBeforeFollow"
              :is-expanded="isExpanded"
              :active-panel="activePanel"
              :small-screen="smallScreen"
            />
          </div>
          <!-- Mobile -->
          <div
            :class="[
              'main-header__mobile',
              { 'main-header__mobile--open': showMobile },
            ]"
            v-if="smallScreen"
          >
            <slot name="mobileBanner" :small-screen="smallScreen" />
            <slot
              name="categoryLinks"
              :open="openMenu"
              :close="closeMenuBeforeFollow"
              :is-expanded="isExpanded"
              :active-panel="activePanel"
              :small-screen="smallScreen"
            />
            <slot
              name="socialLinks"
              :open="openMenu"
              :close="closeMenuBeforeFollow"
              :is-expanded="isExpanded"
              :active-panel="activePanel"
              :small-screen="smallScreen"
            />
            <slot name="headerSocialIcons" :small-screen="smallScreen" />
          </div>
          <div class="main-header__clickable">
            <slot name="logo" />
          </div>
          <div class="header-links--secondary" v-if="!smallScreen">
            <slot
              name="socialLinks"
              :open="openMenu"
              :close="closeMenuBeforeFollow"
              :is-expanded="isExpanded"
              :active-panel="activePanel"
              :small-screen="smallScreen"
            />
            <slot name="newsletterHeaderRedirect" />
          </div>
        </nav>
        <div class="main-header__right">
          <slot
            name="control"
            :searchToggle="searchToggle"
            :is-expanded="isExpanded"
            :active-panel="activePanel"
          />
        </div>
      </div>
    </div>
    <div class="main-header__subcategories-menu" v-clickoutside="close">
      <div
        :class="[
          'header-panel',
          { 'header-panel--open': activePanel },
          { 'primary-header': shouldIHideRightBlock },
        ]"
        v-if="activePanel"
      >
        <div class="header-panel__wrapper container--full" v-if="!searchShow">
          <slot
            name="categoryLinks"
            :open="openMenu"
            :close="closeMenuBeforeFollow"
            :is-expanded="isExpanded"
            :active-panel="activePanel"
          />
          <slot
            name="socialLinks"
            :open="openMenu"
            :close="closeMenuBeforeFollow"
            :is-expanded="isExpanded"
            :active-panel="activePanel"
          />
          <slot name="panel" :active-panel="activePanel" />
        </div>
        <slot name="panelSearch" :active-panel="activePanel" />
      </div>
    </div>
    <!-- no-js only -->
    <slot />
  </div>
</template>

<script>
  import { clickoutside } from '@Foundation/directives/clickoutside/clickoutside';
  import { scrollPosition, throttle } from '@Foundation/utilities';

  export default {
    name: 'main-header',
    directives: {
      clickoutside,
    },
    data() {
      return {
        activePanel: null,
        activeMobile: null,
        preserveOpened: false,
        showMobile: false,
        smallScreen: !window.matchMedia('(min-width: 1200px)').matches,
        body: null,
        overlayShow: false,
        overflowClass: 'body-hide-overflow',
        searchShow: false,
        hideRightBlock: false,
      };
    },
    computed: {
      shouldIHideRightBlock() {
        return this.hideRightBlock;
      },
    },
    mounted() {
      this.body = document.querySelector('body');
      /* istanbul ignore next */
      this.scrollEventAdd();
      this.updateSmallScreenOnResize();
    },
    methods: {
      closeMenuBeforeFollow(event) {
        this.burger();
        setTimeout(() => {
          window.location.href = event.target.href;
        }, 300);
      },
      burger() {
        this.searchShow = this.setValueIfTrue(this.searchShow, false);
        this.activePanel = null;
        this.toggleMobile();
        this.overlayToggle(this.showMobile);
      },
      searchToggle(id) {
        this.toggleSearch();
        this.showMobile = this.setValueIfTrue(this.showMobile, false);
        if (this.searchShow) {
          this.overlayToggle(true);
          this.preserveOpened = true;
          this.activePanel = id;
          this.focusSearchBar();
        } else {
          this.overlayToggle(false);
          this.activePanel = null;
        }
      },
      toggleSearch() {
        this.searchShow = !this.searchShow;
      },
      toggleMobile() {
        this.showMobile = !this.showMobile;
      },
      focusSearchBar() {
        this.$nextTick(() => {
          const searchField = document.getElementById('search-field');
          if (!searchField) {
            return;
          }
          searchField.focus();
        });
      },
      setValueIfTrue(condition, val) {
        return condition ? val : condition;
      },
      openMenu(id) {
        /* istanbul ignore next */
        if (this.smallScreen) {
          this.handleAccordion(id);
          return;
        }
        // document.querySelector('panel-'+id).focus()
        this.preserveOpened = true;
        this.activePanel = id;
        this.overlayToggle(true);
        this.searchShow = false;

        const linkItem = this.$el.querySelector(`[id='${id}']`);
        if (linkItem && linkItem.closest('.header-links--primary')) {
          this.hideRightBlock = true;
        } else {
          this.hideRightBlock = false;
        }
      },
      close() {
        if (this.smallScreen) {
          this.activeMobile = null;
          return;
        }
        if (!this.preserveOpened) {
          this.activePanel = null;
          this.overlayToggle(false);
          this.searchShow = false;
        }
        setTimeout(() => {
          this.preserveOpened = false;
        }, 100);
      },
      closeOnEsc() {
        return this.activePanel ? this.close() : null;
      },
      isExpanded(id) {
        const active = this.smallScreen ? this.activeMobile : this.activePanel;
        return id === active ? 'true' : 'false';
      },
      handleAccordion(id) {
        this.activeMobile = id;

        const elemClass = 'categories__item--open';
        const elem = document.getElementById(id);

        if (!elem) {
          return;
        }

        const panelElem = document.getElementById(`panel-${id}`);
        const parentElem = elem.closest('.categories__item');
        const parentActive = parentElem.classList.contains(elemClass);

        /* istanbul ignore next */
        if (!parentActive) {
          this.collapseAll(elemClass);
          this.updateAriaHidden();
        }

        parentElem.classList.toggle(elemClass);
        panelElem.setAttribute('aria-hidden', parentActive);
        elem.setAttribute('aria-expanded', !parentActive);
      },
      collapseAll(elemClass) {
        const categories = document.querySelectorAll('.categories__item');
        /* istanbul ignore next */
        Array.prototype.forEach.call(categories, function (item) {
          item.classList.remove(elemClass);
        });
      },
      updateAriaHidden() {
        const subcategories = document.querySelectorAll('.subcategories');
        /* istanbul ignore next */
        Array.prototype.forEach.call(subcategories, function (item) {
          item.setAttribute('aria-hidden', true);
        });
      },

      /**
       * Open or close the overlay
       * Apply a class to the body to enable scrolling
       * @param {boolean} show - True to open, false to close
       * @returns {undefined}
       */
      overlayToggle(show) {
        this.overlayShow = show;

        /* istanbul ignore next */
        if (show) {
          this.body.classList.add(this.overflowClass);
        } else {
          this.body.classList.remove(this.overflowClass);
        }
      },

      /**
       * Handles showing or hiding the bar when scrolling
       * @param {number} scrollPos - Scroll position on time of request
       * @returns {number} Current scroll position
       */
      scrollEventHandle(scrollPos) {
        if (!this.$refs.mainMenu || this.showMobile) {
          return null;
        }
        const body = document.querySelector('body');
        const classnames = {
          sticky: 'is-sticky',
          stickyHidden: 'is-sticky--hidden',
        };
        const navHeight = this.$refs.mainMenu.getBoundingClientRect().height;
        const currentScrollPos = scrollPosition();
        const isScrollingDown = currentScrollPos > scrollPos;
        if (!isScrollingDown && currentScrollPos > navHeight) {
          this.$refs.mainMenu.classList.add(`${classnames.sticky}`);
          this.$refs.mainMenu.classList.remove(`${classnames.stickyHidden}`);
          body.style.paddingTop = `${navHeight}px`;
          this.$root.$emit('menu-hidden', false);
        } else if (isScrollingDown && currentScrollPos > navHeight) {
          this.$refs.mainMenu.classList.add(`${classnames.stickyHidden}`);
          this.$root.$emit('menu-hidden', true);
        } else {
          this.$refs.mainMenu.classList.remove(`${classnames.sticky}`);
          this.$refs.mainMenu.classList.remove(`${classnames.stickyHidden}`);
          body.style.paddingTop = 0;
        }

        return currentScrollPos;
      },

      /**
       * Setup event listener for scrolling (required for sticky bar)
       * @returns {undefined}
       */
      scrollEventAdd() {
        let scrollPos = scrollPosition();
        window.addEventListener(
          'scroll',
          throttle(() => {
            scrollPos = this.scrollEventHandle(scrollPos);
          }, 160)
        );
      },

      /**
       * Event listener for keeping smallScreen current if screen size changes
       * @returns {true or false}
       */
      updateSmallScreenOnResize() {
        /* istanbul ignore next */
        window.addEventListener(
          'resize',
          throttle(() => {
            this.smallScreen = !window.matchMedia('(min-width: 1200px)')
              .matches;
          }, 100)
        );
      },
    },
  };
</script>
<style src="./MainHeader.scss" lang="scss"></style>
